import {
  type Analytics,
  getAnalytics,
  isSupported as isAnalyticsSupported,
  setUserId as setAnalyticsUserId,
  setUserProperties as setAnalyticsUserProperties,
} from "firebase/analytics"
import { type FirebaseApp, initializeApp } from "firebase/app"
import {
  type Auth,
  type User as FirebaseAuthUser,
  getAuth,
  onIdTokenChanged,
  signOut,
} from "firebase/auth"
import {
  type DocumentData,
  getDoc,
  getFirestore,
  onSnapshot,
} from "firebase/firestore"
import { type Functions, getFunctions, httpsCallable } from "firebase/functions"
import type React from "react"
import { type ReactNode, useEffect, useState } from "react"

import type { Role } from "@timebanken/services"
import {
  getGlobalSettingsDocRef,
  getTenantConfigDocRef,
  getUserPublicProfileDocRef,
  subscribeAssignedRole,
  withCurrentRoleTerminology,
} from "@timebanken/services"
import {
  reportConnectionError,
  reportConnectionSuccess,
} from "./ConnectionStatusContext"
import { FirebaseContext, type FirebaseContextType } from "./FirebaseContext"

// --- Type Definitions ---
interface UserPublicProfileData extends DocumentData {
  status?: string
  annualHourOverrides?: { [year: string]: number }
  firstName?: string
  lastName?: string
  phone?: string
  phoneShareConsent?: boolean | null
}
interface GlobalAnnualHoursData extends DocumentData {
  [year: string]: number
}

// --- Firebase Initialization ---
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "dummy-api-key",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    "dummy-project.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "dummy-project",
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "dummy-project.appspot.com",
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "1234567890",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:1234567890:web:dummyapp",
}

const app: FirebaseApp = initializeApp(firebaseConfig)
const firebaseAuth: Auth = getAuth(app)
const firebaseFunctions: Functions = getFunctions(app, "europe-north1")

// Pages load activities through this callable. Waking it at sign-in lets a
// cold start and the database connection overlap the profile and role reads
// instead of following them. A warm-up call reads no tenant data.
// Directory instances take one request each, so a warm-up there would only
// occupy the instance that the page's own requests cannot share.
const wakePageDataCallables = (databaseId: string) => {
  httpsCallable(
    firebaseFunctions,
    "listAccessibleActivities",
  )({ databaseId, warmup: true }).catch(() => undefined)
}

// --- Analytics (Production Only) ---
const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID

// Initialize analytics only if measurement ID is configured (production)
const initializeAnalytics = async () => {
  if (!measurementId) {
    return null
  }
  try {
    const supported = await isAnalyticsSupported()
    if (supported) {
      return getAnalytics(app)
    }
  } catch (error) {
    console.warn("Firebase Analytics not supported:", error)
  }
  return null
}

// Analytics loads Google's tag script, so it starts once the page is idle
// instead of competing with the application's startup requests.
const whenPageIdle = () =>
  new Promise<void>((resolve) => {
    const start = () => {
      if ("requestIdleCallback" in window)
        window.requestIdleCallback(() => resolve(), { timeout: 5000 })
      else setTimeout(resolve, 2000)
    }
    if (document.readyState === "complete") start()
    else window.addEventListener("load", start, { once: true })
  })

const analyticsReady: Promise<Analytics | null> =
  measurementId && typeof window !== "undefined"
    ? whenPageIdle().then(initializeAnalytics)
    : Promise.resolve(null)

// Get Firestore instance with specific database ID
const getFirestoreForDatabase = (dbId = "(default)") => {
  return dbId === "(default)" ? getFirestore(app) : getFirestore(app, dbId)
}

// --- Provider Component ---
interface FirebaseProviderProps {
  children: ReactNode
  databaseId?: string // Optional database ID for multi-tenant support
}

const applyFirebaseTenantProfile = ({
  hasSuperAdminClaim,
  publicProfileSnap,
  setUserRole,
  setUserAnnualHourOverrides,
  setUserFirstName,
  setUserLastName,
}: {
  hasSuperAdminClaim: boolean
  publicProfileSnap: import("firebase/firestore").DocumentSnapshot<
    DocumentData,
    DocumentData
  >
  setUserRole: React.Dispatch<React.SetStateAction<Role | null>>
  setUserAnnualHourOverrides: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setUserFirstName: React.Dispatch<React.SetStateAction<string | null>>
  setUserLastName: React.Dispatch<React.SetStateAction<string | null>>
}) => {
  let userRoleObject: Role

  // Super-admin via custom claims takes priority
  if (hasSuperAdminClaim) {
    userRoleObject = {
      id: "super-admin",
      displayName: "Super Administrator",
      systemRole: "super-admin",
      standardHours: 0, // V3: Exempt from hours (replaces isExemptFromHours)
      permissions: ["*"], // V3: Wildcard permission
    } as Role
  } else {
    // The live assignment listener resolves the tenant role, and route
    // guards wait for it, so a one-time read here would be discarded.
    userRoleObject = {
      id: "unassigned",
      displayName: "Ingen rolle",
      permissions: [],
    }
  }

  const publicProfileData = (
    publicProfileSnap.exists() ? publicProfileSnap.data() : null
  ) as UserPublicProfileData | null

  setUserRole(userRoleObject)

  // Super-admins have active tenant access even without a tenant profile.
  const isSuperAdmin = hasSuperAdminClaim

  let status: string | null = null
  if (isSuperAdmin) {
    status = "active"
    setUserAnnualHourOverrides({})
    setUserFirstName(publicProfileData?.firstName || null)
    setUserLastName(publicProfileData?.lastName || null)
  } else if (publicProfileData) {
    status = publicProfileData.status || null
    setUserAnnualHourOverrides(publicProfileData.annualHourOverrides || {})
    setUserFirstName(publicProfileData.firstName || null)
    setUserLastName(publicProfileData.lastName || null)
  } else {
    setUserAnnualHourOverrides({})
    setUserFirstName(null)
    setUserLastName(null)
  }

  return { publicProfileData, status }
}

const useFirebaseIdentitySession = ({
  databaseId,
  setUser,
  setIsLoadingAssignment,
  setUserRole,
  setIsPlatformAdministrator,
  setManagedTeamIds,
  setUserId,
  setIsLoadingUser,
  setConfig,
  setUserAnnualHourOverrides,
  setUserFirstName,
  setUserLastName,
  setPhoneShareConsent,
  setHasPhone,
  setGlobalAnnualHours,
  setUserStatus,
  setIsAuthReady,
}: {
  databaseId: string
  setUser: React.Dispatch<React.SetStateAction<FirebaseAuthUser | null>>
  setIsLoadingAssignment: React.Dispatch<React.SetStateAction<boolean>>
  setUserRole: React.Dispatch<React.SetStateAction<Role | null>>
  setIsPlatformAdministrator: React.Dispatch<React.SetStateAction<boolean>>
  setManagedTeamIds: React.Dispatch<React.SetStateAction<string[]>>
  setUserId: React.Dispatch<React.SetStateAction<string | null>>
  setIsLoadingUser: React.Dispatch<React.SetStateAction<boolean>>
  setConfig: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >
  setUserAnnualHourOverrides: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setUserFirstName: React.Dispatch<React.SetStateAction<string | null>>
  setUserLastName: React.Dispatch<React.SetStateAction<string | null>>
  setPhoneShareConsent: React.Dispatch<
    React.SetStateAction<boolean | null | undefined>
  >
  setHasPhone: React.Dispatch<React.SetStateAction<boolean>>
  setGlobalAnnualHours: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setUserStatus: React.Dispatch<React.SetStateAction<string | null>>
  setIsAuthReady: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  useEffect(() => {
    const db = getFirestoreForDatabase(databaseId)
    let generation = 0
    // The identity whose tenant data is loaded, so hourly token renewals do not
    // tear down the application. Tenant roles come from the live assignment
    // listener; the token only carries the platform administrator claim.
    let loaded: { uid: string; superAdmin: boolean } | null = null
    const unsubscribe = onIdTokenChanged(
      firebaseAuth,
      async (currentUser: FirebaseAuthUser | null) => {
        const currentGeneration = ++generation
        if (currentUser && currentUser.uid === loaded?.uid) {
          const previous = loaded
          try {
            const { claims } = await currentUser.getIdTokenResult()
            if (currentGeneration !== generation) return
            if ((claims?.superAdmin === true) === previous.superAdmin) {
              setUser(currentUser)
              return
            }
          } catch {
            if (currentGeneration !== generation) return
          }
          // A changed or unreadable claim reloads the session from scratch.
        }
        loaded = null
        setIsLoadingAssignment(false)
        setUserRole(null)
        setIsPlatformAdministrator(false)
        setManagedTeamIds([])
        setUser(currentUser)
        if (currentUser) {
          wakePageDataCallables(databaseId)
          setUserId(currentUser.uid)
          setIsLoadingUser(true)
          let hasSuperAdminClaim = false
          try {
            const userPublicProfileRef = getUserPublicProfileDocRef(
              db,
              currentUser.uid,
            )
            const globalAnnualHoursRef = getGlobalSettingsDocRef(db)
            const configRef = getTenantConfigDocRef(db)

            // Claims and tenant documents are independent, so read them together.
            const [
              idTokenResult,
              publicProfileSnap,
              globalAnnualHoursSnap,
              configSnap,
            ] = await Promise.all([
              currentUser.getIdTokenResult(),
              getDoc(userPublicProfileRef),
              getDoc(globalAnnualHoursRef),
              getDoc(configRef),
            ])
            if (currentGeneration !== generation) return
            hasSuperAdminClaim = idTokenResult.claims?.superAdmin === true
            setIsPlatformAdministrator(hasSuperAdminClaim)

            if (configSnap.exists()) {
              setConfig(configSnap.data())
            }

            const { publicProfileData, status } = applyFirebaseTenantProfile({
              hasSuperAdminClaim,
              publicProfileSnap,
              setUserRole,
              setUserAnnualHourOverrides,
              setUserFirstName,
              setUserLastName,
            })

            // GDPR consent state from public profile
            setPhoneShareConsent(
              publicProfileData?.phoneShareConsent ?? undefined,
            )
            setHasPhone(!!publicProfileData?.phone)

            setGlobalAnnualHours(
              (globalAnnualHoursSnap.exists()
                ? globalAnnualHoursSnap.data()
                : {}) as GlobalAnnualHoursData,
            )
            setUserStatus(status)
            loaded = { uid: currentUser.uid, superAdmin: hasSuperAdminClaim }
          } catch (error) {
            if (currentGeneration !== generation) return
            console.error("Error fetching user data in FirebaseContext:", error)
            // V3: Default user role with permissions array
            setUserRole({
              id: "user",
              displayName: "User",
              systemRole: "user",
              permissions: [],
            } as Role)
            setUserStatus(null)
            setManagedTeamIds([])
            setUserAnnualHourOverrides({})
            setGlobalAnnualHours({})
            setPhoneShareConsent(undefined)
            setHasPhone(false)
          } finally {
            if (currentGeneration === generation) {
              // The live assignment listener starts after this render. Mark it
              // pending in the same batch, or the shell mounts once with the
              // initial role and then unmounts while the listener resolves.
              if (!hasSuperAdminClaim) setIsLoadingAssignment(true)
              setIsLoadingUser(false)
            }
          }
        } else {
          setIsPlatformAdministrator(false)
          setUserId(null)
          setUserRole(null)
          setUserStatus(null)
          setUserFirstName(null)
          setUserLastName(null)
          setIsLoadingUser(false)
          setUserAnnualHourOverrides({})
          setGlobalAnnualHours({})
          setConfig(null)
          setPhoneShareConsent(undefined)
          setHasPhone(false)
        }
        if (currentGeneration === generation) setIsAuthReady(true)
      },
    )
    return () => {
      ++generation
      unsubscribe()
    }
  }, [
    databaseId,
    setUserRole,
    setUser,
    setPhoneShareConsent,
    setUserLastName,
    setUserStatus,
    setUserId,
    setUserFirstName,
    setUserAnnualHourOverrides,
    setIsLoadingUser,
    setManagedTeamIds,
    setIsLoadingAssignment,
    setGlobalAnnualHours,
    setIsPlatformAdministrator,
    setIsAuthReady,
    setHasPhone,
    setConfig,
  ])
}

const useFirebaseRoleAndHoursSubscriptions = ({
  userId,
  isLoadingUser,
  isPlatformAdministrator,
  databaseId,
  setUserRole,
  setManagedTeamIds,
  setIsLoadingAssignment,
  setGlobalAnnualHours,
}: {
  userId: string | null
  isLoadingUser: boolean
  isPlatformAdministrator: boolean
  databaseId: string
  setUserRole: React.Dispatch<React.SetStateAction<Role | null>>
  setManagedTeamIds: React.Dispatch<React.SetStateAction<string[]>>
  setIsLoadingAssignment: React.Dispatch<React.SetStateAction<boolean>>
  setGlobalAnnualHours: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
}) => {
  useEffect(() => {
    if (!userId || isLoadingUser || isPlatformAdministrator) return
    return subscribeAssignedRole(
      getFirestoreForDatabase(databaseId),
      userId,
      (role) =>
        setUserRole(
          role
            ? withCurrentRoleTerminology(role)
            : { id: "unassigned", displayName: "Ingen rolle", permissions: [] },
        ),
      (error) => console.error("Could not refresh role assignment:", error),
      setManagedTeamIds,
      setIsLoadingAssignment,
    )
  }, [
    userId,
    databaseId,
    isLoadingUser,
    isPlatformAdministrator,
    setUserRole,
    setManagedTeamIds,
    setIsLoadingAssignment,
  ])

  useEffect(() => {
    const db = getFirestoreForDatabase(databaseId)
    const globalAnnualHoursRef = getGlobalSettingsDocRef(db)
    const unsubscribe = onSnapshot(
      globalAnnualHoursRef,
      (docSnap) => {
        setGlobalAnnualHours(
          (docSnap.exists() ? docSnap.data() : {}) as GlobalAnnualHoursData,
        )
        // Report successful connection
        reportConnectionSuccess()
      },
      (error) => {
        console.error("Error subscribing to global annual hours:", error)
        // Report connection error (may indicate ad blocker)
        reportConnectionError(error)
      },
    )
    return () => unsubscribe()
  }, [databaseId, setGlobalAnnualHours])
}

const useFirebaseProfileSubscriptions = ({
  userId,
  isLoadingUser,
  isPlatformAdministrator,
  databaseId,
  setUserRole,
  setManagedTeamIds,
  setIsLoadingAssignment,
  setGlobalAnnualHours,
  setPhoneShareConsent,
  setHasPhone,
  setUserFirstName,
  setUserLastName,
  setConfig,
}: {
  userId: string | null
  isLoadingUser: boolean
  isPlatformAdministrator: boolean
  databaseId: string
  setUserRole: React.Dispatch<React.SetStateAction<Role | null>>
  setManagedTeamIds: React.Dispatch<React.SetStateAction<string[]>>
  setIsLoadingAssignment: React.Dispatch<React.SetStateAction<boolean>>
  setGlobalAnnualHours: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setPhoneShareConsent: React.Dispatch<
    React.SetStateAction<boolean | null | undefined>
  >
  setHasPhone: React.Dispatch<React.SetStateAction<boolean>>
  setUserFirstName: React.Dispatch<React.SetStateAction<string | null>>
  setUserLastName: React.Dispatch<React.SetStateAction<string | null>>
  setConfig: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >
}) => {
  useFirebaseRoleAndHoursSubscriptions({
    userId,
    isLoadingUser,
    isPlatformAdministrator,
    databaseId,
    setUserRole,
    setManagedTeamIds,
    setIsLoadingAssignment,
    setGlobalAnnualHours,
  })

  // Real-time listener for user public profile (GDPR consent, phone, name updates)
  useEffect(() => {
    if (!userId) {
      setPhoneShareConsent(undefined)
      setHasPhone(false)
      return
    }
    const db = getFirestoreForDatabase(databaseId)
    const userPublicProfileRef = getUserPublicProfileDocRef(db, userId)
    const unsubscribe = onSnapshot(
      userPublicProfileRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as UserPublicProfileData
          setPhoneShareConsent(data.phoneShareConsent ?? undefined)
          setHasPhone(!!data.phone)
          setUserFirstName(data.firstName || null)
          setUserLastName(data.lastName || null)
        }
      },
      (error) => {
        console.error("Error subscribing to user public profile:", error)
      },
    )
    return () => unsubscribe()
  }, [
    userId,
    databaseId,
    setPhoneShareConsent,
    setUserLastName,
    setUserFirstName,
    setHasPhone,
  ])

  // Real-time listener for config (tier cache, feature flags)
  useEffect(() => {
    const db = getFirestoreForDatabase(databaseId)
    const configRef = getTenantConfigDocRef(db)
    const unsubscribe = onSnapshot(
      configRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setConfig(docSnap.data())
        } else {
          setConfig(null)
        }
        // Report successful connection
        reportConnectionSuccess()
      },
      (error) => {
        console.error("Error subscribing to config:", error)
        // Report connection error (may indicate ad blocker)
        reportConnectionError(error)
      },
    )
    return () => unsubscribe()
  }, [databaseId, setConfig])
}

const useFirebaseSessionAnalytics = ({
  isLoggingOut,
  setIsLoggingOut,
  userId,
  databaseId,
  userRole,
}: {
  isLoggingOut: boolean
  setIsLoggingOut: React.Dispatch<React.SetStateAction<boolean>>
  userId: string | null
  databaseId: string
  userRole: Role | null
}) => {
  useEffect(() => {
    if (isLoggingOut) {
      signOut(firebaseAuth).finally(() => {
        setIsLoggingOut(false)
      })
    }
  }, [isLoggingOut, setIsLoggingOut])

  // Set analytics user properties for tenant tracking
  useEffect(() => {
    let current = true
    void analyticsReady.then((analyticsInstance) => {
      if (!current || !analyticsInstance) return
      if (userId) {
        setAnalyticsUserId(analyticsInstance, userId)
        setAnalyticsUserProperties(analyticsInstance, {
          tenant_id: databaseId,
          user_role: userRole?.systemRole || "unknown",
        })
      } else {
        // Clear user ID on logout
        setAnalyticsUserId(analyticsInstance, null)
      }
    })
    return () => {
      current = false
    }
  }, [userId, databaseId, userRole])

  // GDPR: User needs phone consent if they have a phone but haven't made a choice yet
}

const useFirebaseSessionEffects = ({
  databaseId,
  setUser,
  setIsLoadingAssignment,
  setUserRole,
  setIsPlatformAdministrator,
  setManagedTeamIds,
  setUserId,
  setIsLoadingUser,
  setConfig,
  setUserAnnualHourOverrides,
  setUserFirstName,
  setUserLastName,
  setPhoneShareConsent,
  setHasPhone,
  setGlobalAnnualHours,
  setUserStatus,
  setIsAuthReady,
  userId,
  isLoadingUser,
  isPlatformAdministrator,
  setIsLoggingOut,
  isLoggingOut,
  userRole,
}: {
  databaseId: string
  setUser: React.Dispatch<React.SetStateAction<FirebaseAuthUser | null>>
  setIsLoadingAssignment: React.Dispatch<React.SetStateAction<boolean>>
  setUserRole: React.Dispatch<React.SetStateAction<Role | null>>
  setIsPlatformAdministrator: React.Dispatch<React.SetStateAction<boolean>>
  setManagedTeamIds: React.Dispatch<React.SetStateAction<string[]>>
  setUserId: React.Dispatch<React.SetStateAction<string | null>>
  setIsLoadingUser: React.Dispatch<React.SetStateAction<boolean>>
  setConfig: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >
  setUserAnnualHourOverrides: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setUserFirstName: React.Dispatch<React.SetStateAction<string | null>>
  setUserLastName: React.Dispatch<React.SetStateAction<string | null>>
  setPhoneShareConsent: React.Dispatch<
    React.SetStateAction<boolean | null | undefined>
  >
  setHasPhone: React.Dispatch<React.SetStateAction<boolean>>
  setGlobalAnnualHours: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  setUserStatus: React.Dispatch<React.SetStateAction<string | null>>
  setIsAuthReady: React.Dispatch<React.SetStateAction<boolean>>
  userId: string | null
  isLoadingUser: boolean
  isPlatformAdministrator: boolean
  setIsLoggingOut: React.Dispatch<React.SetStateAction<boolean>>
  isLoggingOut: boolean
  userRole: Role | null
}) => {
  useFirebaseIdentitySession({
    databaseId,
    setUser,
    setIsLoadingAssignment,
    setUserRole,
    setIsPlatformAdministrator,
    setManagedTeamIds,
    setUserId,
    setIsLoadingUser,
    setConfig,
    setUserAnnualHourOverrides,
    setUserFirstName,
    setUserLastName,
    setPhoneShareConsent,
    setHasPhone,
    setGlobalAnnualHours,
    setUserStatus,
    setIsAuthReady,
  })

  useFirebaseProfileSubscriptions({
    userId,
    isLoadingUser,
    isPlatformAdministrator,
    databaseId,
    setUserRole,
    setManagedTeamIds,
    setIsLoadingAssignment,
    setGlobalAnnualHours,
    setPhoneShareConsent,
    setHasPhone,
    setUserFirstName,
    setUserLastName,
    setConfig,
  })

  const logout = () => {
    setIsLoggingOut(true)
  }

  useFirebaseSessionAnalytics({
    isLoggingOut,
    setIsLoggingOut,
    userId,
    databaseId,
    userRole,
  })

  return { logout }
}

export const FirebaseProvider: React.FC<FirebaseProviderProps> = ({
  children,
  databaseId = "(default)",
}) => {
  const [user, setUser] = useState<FirebaseAuthUser | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<Role | null>(null)
  const [userStatus, setUserStatus] = useState<string | null>(null)
  const [userFirstName, setUserFirstName] = useState<string | null>(null)
  const [userLastName, setUserLastName] = useState<string | null>(null)
  const [isAuthReady, setIsAuthReady] = useState<boolean>(false)
  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(true)
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false)
  const [isPlatformAdministrator, setIsPlatformAdministrator] = useState(false)
  const [managedTeamIds, setManagedTeamIds] = useState<string[]>([])
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false)
  const [userAnnualHourOverrides, setUserAnnualHourOverrides] = useState<
    Record<string, number>
  >({})
  const [globalAnnualHours, setGlobalAnnualHours] = useState<
    Record<string, number>
  >({})
  const [config, setConfig] = useState<Record<string, unknown> | null>(null)
  const [phoneShareConsent, setPhoneShareConsent] = useState<
    boolean | null | undefined
  >(undefined)
  const [hasPhone, setHasPhone] = useState<boolean>(false)

  const { logout } = useFirebaseSessionEffects({
    databaseId,
    setUser,
    setIsLoadingAssignment,
    setUserRole,
    setIsPlatformAdministrator,
    setManagedTeamIds,
    setUserId,
    setIsLoadingUser,
    setConfig,
    setUserAnnualHourOverrides,
    setUserFirstName,
    setUserLastName,
    setPhoneShareConsent,
    setHasPhone,
    setGlobalAnnualHours,
    setUserStatus,
    setIsAuthReady,
    userId,
    isLoadingUser,
    isPlatformAdministrator,
    setIsLoggingOut,
    isLoggingOut,
    userRole,
  })

  const needsPhoneConsent =
    hasPhone && (phoneShareConsent === null || phoneShareConsent === undefined)

  const db = getFirestoreForDatabase(databaseId)
  const value: FirebaseContextType = {
    db,
    auth: firebaseAuth,
    userId,
    user,
    userRole,
    isPlatformAdministrator,
    managedTeamIds,
    userStatus,
    userFirstName,
    userLastName,
    isAuthReady,
    // Route guards must wait for the live assignment's role read to settle.
    isLoadingUser: isLoadingUser || isLoadingAssignment,
    isLoggingOut,
    logout,
    userAnnualHourOverrides,
    globalAnnualHours,
    functions: firebaseFunctions,
    databaseId,
    config,
    phoneShareConsent,
    hasPhone,
    needsPhoneConsent,
  }

  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-xl font-semibold animate-pulse">Laster...</p>
      </div>
    )
  }

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  )
}

export { initializeApp as initializeFirebase }
export { FirebaseContext, useAuth, useFirebase } from "./FirebaseContext"
export * from "./TenantContext"
export { usePermissions } from "./usePermissions"
export type { UsePermissionsReturn } from "./usePermissions"
export {
  ConnectionStatusProvider,
  useConnectionStatus,
  useConnectionReporter,
  isAdBlockerError,
  reportConnectionError,
  reportConnectionSuccess,
} from "./ConnectionStatusContext"
export type { ConnectionStatus } from "./ConnectionStatusContext"
